// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapLink, BootstrapNav } from '../../dsl/front';
import { isRelated } from '../../services';
import { AbstractModuleUi } from '../_abstract/ui';
import { AppTheme } from '../_contracts';
import { LineHeader } from '../_components/ui';
import { getArticlesCount, getArticlesCountForTab, getArticlesCountPerTab, isTeaserBoxWithoutTabs } from './TeaserBox.helpers';
import { teaserBoxInnerGrids } from './_inner-grids';
import { TeaserBoxVersion } from './TeaserBox.contracts';
import { prepareImage } from '../_filters';
import { LineHeaderContent } from './_partials/LineHeaderContent';
/**
 * Component capable of rendering the `TeaserBoxModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Michał Rossian <michal.rossian@movecloser.pl>
 */
let TeaserBoxModuleUi = class TeaserBoxModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        //
        this.AppTheme = AppTheme;
        /**
         * Currently-active tab.
         */
        this.activeTab = '';
        this.headerImage = null;
        this.headerLink = null;
        /**
         * Tabs' content (resolved using `RelatedService`).
         */
        this.tabs = [];
        this.tabsOrder = [];
    }
    /**
     * @inheritDoc
     */
    get articlesCount() {
        return getArticlesCount(this.moduleVersionFallback, this.tabs.length, this.teaserBoxConfig);
    }
    get moduleVersionFallback() {
        switch (this.module.version) {
            case TeaserBoxVersion['L-4S-text-list-tabs']:
                return this.tabs.length === 2 ? TeaserBoxVersion['L-5S-text-list'] : TeaserBoxVersion['L-2S-text-list'];
            case TeaserBoxVersion['L-4S-text-list']:
            case TeaserBoxVersion['XL-slider-3S-text-list']:
                return TeaserBoxVersion['L-5S-text-list'];
            case TeaserBoxVersion['XL-3S-text-list']:
                return TeaserBoxVersion['L-2S-text-list'];
            default:
                return this.module.version;
        }
    }
    /**
     * Component capable of rendering the contents from one, specific `TeaserBoxVersion`.
     */
    get component() {
        const component = teaserBoxInnerGrids.find(grid => grid.moduleVersion === this.moduleVersionFallback);
        if (typeof component !== 'function') {
            throw new Error(`TeaserBoxModuleUi.[get]component: Failed to resolve the applicable [TeaserBoxInnerGrid] component for the given version! Current [module.version]: [${this.module.version}].`);
        }
        return component;
    }
    get lineHeaderTitleTag() {
        if (this.module.content.header) {
            return this.module.content.header.isH1 ? 'h1' : 'h2';
        }
        return 'h2';
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        try {
            if (this.module.content.tabs.length > 0) {
                const itemsToResolve = Array.isArray(this.module.content.tabs) ? this.module.content.tabs : [];
                this.tabsOrder = itemsToResolve.map((item, index) => {
                    const prefix = this.$id('tab');
                    return {
                        id: `${prefix}-${index}`,
                        label: item.label
                    };
                });
                this.tabs = this.tabsOrder;
                this.activeTab = this.tabs[0].id;
            }
            this.onReady();
            if (this.module.content.headerLink) {
                const link = await this.relatedService.resolve(this.module.content.headerLink);
                this.headerLink = link.urlPath;
            }
            if (this.module.content.headerImage) {
                const imageFile = await this.relatedService.resolve(this.module.content.headerImage);
                this.headerImage = prepareImage(imageFile, this.imageRatio);
            }
            await this.composeTabs();
        }
        catch (e) {
            this.onError(e);
        }
        if (this.tabs.length > 0) {
            this.activeTab = this.tabs[0].id;
            this.onReady();
        }
        else {
            this.onError();
        }
    }
    /**
     * Determines whether there's a header text to render.
     */
    get hasHeaderText() {
        return typeof this.module.content.header !== 'undefined' &&
            typeof this.module.content.header.text === 'string' &&
            this.module.content.header.text.length > 0;
    }
    /**
     * Determines whether the module has multiple tabs to render.
     */
    get hasMultipleTabs() {
        if (isTeaserBoxWithoutTabs(this.moduleVersionFallback)) {
            return false;
        }
        if (typeof this.tabs === 'undefined' || !Array.isArray(this.tabs)) {
            return false;
        }
        return this.tabs.length > 1;
    }
    get isWithoutTabs() {
        return isTeaserBoxWithoutTabs(this.moduleVersionFallback);
    }
    get tabForWithoutTabs() {
        if (this.tabs.length !== 2) {
            return null;
        }
        const [firstTab, secondTab] = this.tabs;
        let articles = [];
        if (secondTab.articles) {
            articles = firstTab.articles.concat(secondTab.articles);
        }
        return {
            ...firstTab,
            articles: articles,
            readMoreLink: secondTab.readMoreLink
        };
    }
    /**
     * Determines whether tha tab of a passed-in label is the currently-active one.
     *
     * @param label - The label of the tab to check.
     */
    isTabActive(label) {
        return this.activeTab === label;
    }
    /**
     * Array of items to be rendered by the `<BootstrapNav>` component.
     */
    get navItems() {
        return this.tabs.map(({ id, label }) => ({
            disabled: false,
            id,
            label
        }));
    }
    /**
     * Determines whether there's a header to be rendered.
     */
    get shouldShowHeader() {
        const { header = {} } = this.module.content;
        if (!this.module.content.showHeader) {
            return false;
        }
        const keys = ['site', 'text'];
        for (const key of keys) {
            const value = header[key];
            if (typeof value === 'string' && value.length > 0) {
                return true;
            }
        }
        return false;
    }
    /**
     * Determines whether the `<LineHeader>` pointer should be shown.
     */
    get shouldShowPointer() {
        if (this.theme === AppTheme.PremiumZone) {
            return false;
        }
        return !!(this.module.content.header && this.module.content.header.text && !this.module.content.headerImage);
    }
    /**
     * Configuration settings for the `<TeaserBox>` component.
     */
    get teaserBoxConfig() {
        const { additionalRows, titleVersion, showAuthorAvatar, showAuthorName, showCategory, showArticleTags, showPublicationDate } = this.module.content;
        return {
            additionalRows,
            showAuthorAvatar,
            showAuthorName,
            showCategory,
            showArticleTags,
            titleVersion,
            showPublicationDate
        }; // FIXME
    }
    /**
     * Resolves length of articles' list displayed by the module.
     */
    static resolveArticlesCount(module, list) {
        const countPerTab = getArticlesCountPerTab(module.version, module.content);
        const listId = list.value.toString();
        return module.content.tabs.reduce((count, tab) => {
            if (!!tab.list && tab.list.value.toString() === listId) {
                count += countPerTab;
            }
            return count;
        }, 0);
    }
    /**
     * Composes the tabs array.
     */
    async composeTabs() {
        const itemsToResolve = Array.isArray(this.module.content.tabs) ? this.module.content.tabs : [];
        if (this.tabsOrder.length === 0) {
            this.tabsOrder = itemsToResolve.map((item, index) => {
                const prefix = this.$id('tab');
                return {
                    id: `${prefix}-${index}`,
                    label: item.label
                };
            });
        }
        await Promise.all(itemsToResolve.map((rawTab, index) => {
            return new Promise((resolve) => {
                this.resolveSingleTab(rawTab, index)
                    .catch(e => {
                    if (this.debug)
                        console.warn(e);
                })
                    .finally(() => {
                    resolve();
                });
            });
        })).finally(() => {
            this.tabs = this.tabsOrder.filter(tab => 'articles' in tab && !!tab.articles);
            this.tabsOrder = [];
        });
    }
    /**
     * Resolves the "read more" link for the given tab.
     *
     * @param readMoreLink - Related link to resolve.
     * @param index
     */
    async resolveReadMoreLink(readMoreLink, index) {
        let readMoreLinkRelatedTarget;
        try {
            readMoreLinkRelatedTarget = await this.relatedService.resolve(readMoreLink, {});
        }
        catch (e) {
            if (this.debug)
                console.warn(e);
        }
        if (typeof readMoreLinkRelatedTarget !== 'undefined') {
            this.tabsOrder[index] = {
                ...this.tabsOrder[index],
                readMoreLink: {
                    target: { path: readMoreLinkRelatedTarget.urlPath }
                }
            };
        }
    }
    /**
     * Resolves all related data for the passed-in tab.
     *
     * @param rawTab - The tab which content is to be resolved.
     * @param index - Tab's index in `this.tabs` array.
     */
    async resolveSingleTab(rawTab, index) {
        const { label, list, readMoreLink, allowDuplicates, startFromBeginning } = rawTab;
        if (typeof list !== 'object' || list === null || !Object.keys(list).length) {
            return;
        }
        let articlesCount = getArticlesCountPerTab(this.moduleVersionFallback, this.teaserBoxConfig);
        if (isTeaserBoxWithoutTabs(this.moduleVersionFallback)) {
            articlesCount = getArticlesCountForTab(this.moduleVersionFallback, index, this.teaserBoxConfig);
        }
        let articles;
        try {
            articles = await this.relatedService
                .resolve(list, {
                continueOrder: !startFromBeginning,
                preventDuplicates: !allowDuplicates,
                take: articlesCount
            });
        }
        catch (e) {
            if (this.debug)
                console.warn(e);
        }
        if (typeof articles === 'undefined') {
            return;
        }
        this.tabsOrder[index] = {
            ...this.tabsOrder[index],
            articles
        };
        if (!isRelated(readMoreLink)) {
            return;
        }
        return await this.resolveReadMoreLink(readMoreLink, index);
    }
};
TeaserBoxModuleUi = __decorate([
    Component({
        name: 'TeaserBoxModuleUi',
        components: {
            BootstrapNav,
            BootstrapLink,
            LineHeader,
            LineHeaderContent
        },
        async created() {
            await this.fetchRelated();
        },
        template: `
    <section v-if="(tabs && tabs.length) || debug"
             :class="['teaser-box', moduleColoredClass, debugClass, loadingClass, versionClass, getThemeByBgClass]"
             :style="{ backgroundColor: customColor }"
    >

    <!-- Heading (Premium Zone only) -->
    <h2 v-if="theme === AppTheme.PremiumZone && hasHeaderText" class="teaser-box__title">
      <LineHeaderContent v-if="theme !== AppTheme.PremiumZone"
                         :text="module.content.header.text" :headerImage="headerImage"/>
    </h2>

    <!-- Header -->
    <LineHeader v-if="shouldShowHeader || hasMultipleTabs" :showPointer="shouldShowPointer"
                :site="module.content.header ? module.content.header.site : null" :theme="getThemeByBg"
                :isBold="module.content.header.isLink || module.content.header.bold || false"
                :tag="lineHeaderTitleTag"
                class="teaser-box__header">
      <!-- Text -->

      <LineHeaderContent v-if="theme !== AppTheme.PremiumZone && hasHeaderText"
                         :text="module.content.header.text" :headerImage="headerImage" :headerLink="headerLink"/>

      <!-- Nav -->
      <BootstrapNav v-if="hasMultipleTabs" v-slot="nav"
                    :activeItem.sync="activeTab" :items="navItems"/>
    </LineHeader>

    <!-- Contents (inner grid) -->
    <template v-if="!isWithoutTabs">
      <component v-if="tab.articles && tab.articles.length" v-for="tab in tabs" :key="tab.id"
                 :is="component"
                 v-bind="{ ...tab, fallbackImage, teaserBoxConfig, relatedService, windowService, isLazy: module.isLazyLoaded, isMobile }"
                 v-show="isTabActive(tab.id)"/>
    </template>
    <template v-else>
      <component v-if="tabForWithoutTabs && tabForWithoutTabs.articles && tabForWithoutTabs.articles.length"
                 :is="component"
                 v-bind="{ ...tabForWithoutTabs, fallbackImage, teaserBoxConfig, relatedService, windowService, isLazy: module.isLazyLoaded, isMobile }"/>
    </template>
    </section>
    <section v-else class="empty-div"></section>
  `
    })
], TeaserBoxModuleUi);
export { TeaserBoxModuleUi };
